import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { resolveApiErrorMessage } from '@/i18n/api-errors'
import { useGeneratorStore } from '@/stores/generatorStore'
import { useInvalidateHistory } from '@/hooks/useHistory'
import { useInvalidateAuthMe } from '@/hooks/useAuthMe'
import { useAuthStore } from '@/stores/authStore'
import { API_BASE_URL } from '@/lib/constants'
import type { QueueItem } from '@/types/queue'
import type { ConfigResponse } from '@/hooks/useConfig'
import type { GenerateImageRequest, GenerateImageResponse } from '@/types/generation'

/** 이미지 생성 진행 단계 (메시지는 i18n 키 - 렌더 시점 번역) */
const PROGRESS_STEPS = [
  { progress: 20, key: 'queue.analyzing' },
  { progress: 40, key: 'queue.generating' },
  { progress: 60, key: 'queue.styling' },
  { progress: 80, key: 'queue.finishing' },
] as const

/** 각 단계 간격 (ms) - 20초 총 소요 예상 (5초 x 4단계) */
const STEP_INTERVAL = 5000

/**
 * 큐 프로세서 훅
 * Generator 페이지에서 한 번만 호출하여 큐를 처리
 */
export function useQueueProcessor() {
  const queue = useGeneratorStore((state) => state.queue)
  const _updateQueueItem = useGeneratorStore((state) => state._updateQueueItem)
  const invalidateHistory = useInvalidateHistory()
  const invalidateAuthMe = useInvalidateAuthMe()
  const queryClient = useQueryClient()

  // 현재 처리 중인 아이템 추적
  const processingRef = useRef<string | null>(null)
  // 프로그레스 타이머 ref
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 프로그레스 시뮬레이션 시작
  const startProgressSimulation = useCallback((queueId: string) => {
    let stepIndex = 0

    // 첫 번째 단계 즉시 적용
    _updateQueueItem(queueId, {
      progress: PROGRESS_STEPS[0].progress,
      progressKey: PROGRESS_STEPS[0].key,
    })
    stepIndex = 1

    progressTimerRef.current = setInterval(() => {
      if (stepIndex < PROGRESS_STEPS.length) {
        _updateQueueItem(queueId, {
          progress: PROGRESS_STEPS[stepIndex].progress,
          progressKey: PROGRESS_STEPS[stepIndex].key,
        })
        stepIndex++
      }
    }, STEP_INTERVAL)
  }, [_updateQueueItem])

  // 프로그레스 시뮬레이션 중지
  const stopProgressSimulation = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  // 단일 아이템 처리
  const processItem = useCallback(async (item: QueueItem) => {
    const { params } = item

    // processing 상태로 변경
    _updateQueueItem(item.id, {
      status: 'processing',
      progress: 0,
      progressKey: 'queue.preparing',
    })

    // 관리자가 장애 공지를 활성화한 경우 사전 차단 (서버 호출 전)
    // 공지 본문은 어드민이 입력한 자유 텍스트라 번역하지 않고 그대로 노출한다
    const cachedConfig = queryClient.getQueryData<ConfigResponse>(['config'])
    if (cachedConfig?.outageNotice?.enabled) {
      _updateQueueItem(item.id, {
        status: 'failed',
        progress: 0,
        progressKey: '',
        error: cachedConfig.outageNotice.message,
        completedAt: Date.now(),
      })
      toast.error(i18n.t('queue.outageTitle'), {
        description: cachedConfig.outageNotice.message,
      })
      return
    }

    // 프로그레스 시뮬레이션 시작
    startProgressSimulation(item.id)

    try {
      // 레퍼런스 이미지가 있으면 요청에 포함
      const referenceImagesPayload = params.referenceImages.length > 0
        ? params.referenceImages.map((img) => ({
            id: img.id,
            s3Key: img.s3Key,
            thumbnailS3Key: img.thumbnailS3Key,
            filename: img.filename,
          }))
        : undefined

      // 템플릿 모드인지 스타일 모드인지에 따라 요청 본문 구성
      const requestBody: GenerateImageRequest = {
        prompt: params.prompt,
        aspectRatio: params.aspectRatio,
        count: params.count,
        referenceImages: referenceImagesPayload,
      }

      if (params.templateId) {
        // 템플릿 모드: templateId 전송
        requestBody.templateId = params.templateId
      } else {
        // 스타일 모드: style 전송
        requestBody.style = params.style
      }

      const response = await fetch(`${API_BASE_URL}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })

      stopProgressSimulation()

      if (!response.ok) {
        // 503: 관리자 장애 공지 활성화로 인한 차단
        if (response.status === 503) {
          // 공지 본문(어드민 자유 텍스트)이 있으면 그대로, 없으면 현지화된 일반 메시지
          let serverMessage = ''
          try {
            const data = await response.json()
            if (typeof data?.message === 'string' && data.message.length > 0) {
              serverMessage = data.message
            }
          } catch {
            // JSON 파싱 실패 시 기본 메시지 사용
          }
          if (!serverMessage) serverMessage = i18n.t('errors.SERVICE_OUTAGE')
          // 캐시된 config 가 최신 outage 상태를 반영하지 않을 수 있으므로 강제 무효화
          queryClient.invalidateQueries({ queryKey: ['config'] })
          _updateQueueItem(item.id, {
            status: 'failed',
            progress: 0,
            progressKey: '',
            error: serverMessage,
            completedAt: Date.now(),
          })
          toast.error(i18n.t('queue.outageTitle'), {
            description: serverMessage,
          })
          return
        }

        if (response.status === 401) {
          // SSO 토큰 만료 - 모달 표시 (logout은 모달에서 확인 클릭 시 처리)
          useGeneratorStore.getState().setSsoTokenExpiredModalOpen(true)

          _updateQueueItem(item.id, {
            status: 'failed',
            progress: 0,
            progressKey: '',
            error: i18n.t('errors.SSO_TOKEN_EXPIRED'),
            completedAt: Date.now(),
          })
          return
        }

        // 400 에러 처리
        if (response.status === 400) {
          try {
            const errorData = await response.json()
            if (errorData.message) {
              const resolved = resolveApiErrorMessage({
                status: 400,
                code: errorData.errorCode,
                params: errorData.params,
                serverMessage: errorData.message,
              })
              _updateQueueItem(item.id, {
                status: 'failed',
                progress: 0,
                progressKey: '',
                error: resolved,
                completedAt: Date.now(),
              })

              if (errorData.errorCode === 'IMAGE_SAFETY') {
                const store = useGeneratorStore.getState()
                store.setSafetyFlaggedCategories(errorData.flaggedCategories ?? [])
                store.setSafetyErrorModalOpen(true)
              } else {
                toast.error(i18n.t('queue.failed'), {
                  description: resolved,
                })
              }
              return
            }
          } catch {
            // JSON 파싱 실패 시 아래 기본 에러 처리로 fallback
          }
        }

        throw new Error('Generation failed')
      }

      const data = (await response.json()) as GenerateImageResponse

      // 성공 처리
      _updateQueueItem(item.id, {
        status: 'completed',
        progress: 100,
        progressKey: 'queue.done',
        generationId: data.id,
        completedAt: Date.now(),
      })

      // 히스토리 캐시 무효화
      invalidateHistory()
      invalidateAuthMe()

      toast.success(i18n.t('queue.successTitle'), {
        description: i18n.t('queue.successDescription', {
          count: data.images?.length || params.count,
        }),
      })

      // 생성 성공 후 hasUsage 체크 (Wrks API 통해 최신 값 확인)
      try {
        const meResponse = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
        })
        if (meResponse.ok) {
          const meData = await meResponse.json()
          if (meData.hasUsage === false) {
            // 사용량 한도 초과 - 모달 표시
            useGeneratorStore.getState().setUsageLimitModalOpen(true)
            // authStore도 업데이트
            const currentUser = useAuthStore.getState().user
            if (currentUser) {
              useAuthStore.getState().setUser({
                ...currentUser,
                hasUsage: false,
              })
            }
          }
        }
      } catch {
        // hasUsage 체크 실패는 무시 (다음 새로고침 시 확인됨)
      }
    } catch (error) {
      stopProgressSimulation()

      _updateQueueItem(item.id, {
        status: 'failed',
        progress: 0,
        progressKey: '',
        error: error instanceof Error && error.message !== 'Generation failed'
          ? error.message
          : i18n.t('errors.UNEXPECTED'),
        completedAt: Date.now(),
      })

      toast.error(i18n.t('queue.failedGeneric'), {
        description: i18n.t('common.tryAgainLater'),
      })
    } finally {
      processingRef.current = null
    }
  }, [_updateQueueItem, startProgressSimulation, stopProgressSimulation, invalidateHistory, invalidateAuthMe, queryClient])

  // 큐 변화 감지 및 처리
  useEffect(() => {
    // 이미 처리 중이면 스킵
    if (processingRef.current) return

    // processing 중인 아이템 확인
    const processingItem = queue.find((item) => item.status === 'processing')
    if (processingItem) {
      // 이미 processing 중인 아이템이 있으면 스킵 (페이지 리로드 등 예외 상황)
      processingRef.current = processingItem.id
      return
    }

    // 첫 번째 pending 아이템 찾기
    const pendingItem = queue.find((item) => item.status === 'pending')
    if (pendingItem) {
      processingRef.current = pendingItem.id
      processItem(pendingItem)
    }
  }, [queue, processItem])

  // 클린업
  useEffect(() => {
    return () => {
      stopProgressSimulation()
    }
  }, [stopProgressSimulation])

  // 큐 상태 계산 헬퍼 반환
  const activeQueueCount = queue.filter(
    (item) => item.status === 'pending' || item.status === 'processing'
  ).length
  const hasProcessingItem = queue.some((item) => item.status === 'processing')
  const isQueueFull = activeQueueCount >= 5 // QUEUE_MAX_SIZE

  return {
    activeQueueCount,
    hasProcessingItem,
    isQueueFull,
  }
}
